import {
  type BlockContent,
  type Call,
  type CallKind,
  type Inline,
  type InlineRun,
  isHeading,
  type NodeContent,
} from './content-model'

// ---------------------------------------------------------------------------
// Reading text out of content
// ---------------------------------------------------------------------------

/**
 * The words in a run, with everything that is not words left out.
 *
 * What a heading says as well as what a passage does — a title is a run and
 * nothing else, so a surface reading one has no block to go through. Exported
 * for that: kept private it was rewritten by hand wherever a title had to be
 * read as text, which is how two answers to "what does this say" come about.
 */
export const runText = (run: InlineRun): string =>
  run
    .map((n) => (n.t === 'text' ? n.v : n.t === 'break' ? ' ' : ''))
    .join('')
    .replace(/\s+/g, ' ')
    .trim()

/**
 * The one run a block is set from, for the surfaces that render a block as a
 * single line of prose.
 *
 * A bilingual block answers with its English side, which is the side those
 * surfaces set; a kind carrying no single run answers empty rather than
 * guessing which of its parts stands for it.
 */
export function runOf(content: BlockContent): InlineRun {
  if ('content' in content) return content.content
  if ('en' in content) return content.en ?? []
  return []
}

/**
 * The parts a node's text is made of, each knowing where its characters came
 * from.
 *
 * `blockText` and `nodeText` are BUILT from this, rather than stating the same
 * assembly a second time. A passage's flattened text is what search matches and
 * what a comment anchors into, so a replace has to offer exactly the passages
 * the search returned — and the only way two matchers cannot disagree is for
 * there to be one. What this adds over the flattener is provenance: which run,
 * which field, and which characters are a JOINER that belongs to no field and
 * therefore cannot be rewritten.
 *
 * A part is one LINE of the flattened text. `dropWhenEmpty` says whether an
 * empty one disappears or prints a blank line, which differs by kind and is not
 * a detail — a comment anchors by offset, so a line that comes and goes moves
 * every anchor beneath it.
 */
export type TextSegment =
  | { of: 'run'; path: string; label: string; run: InlineRun }
  | { of: 'field'; path: string; label: string; value: string }
  /** Punctuation the assembly adds, belonging to no field. */
  | { of: 'joiner'; value: string }

export type TextPart = {
  label: string
  dropWhenEmpty: boolean
  segments: TextSegment[]
}

const runSeg = (path: string, label: string, run: InlineRun): TextSegment => ({
  of: 'run',
  path,
  label,
  run,
})

/** What one part says — the same collapse `runText` applies, per run. */
export const partText = (part: TextPart): string =>
  part.segments
    .map((s) =>
      s.of === 'run' ? runText(s.run) : s.of === 'field' ? s.value : s.value,
    )
    .join('')

const linesOf = (parts: TextPart[]): string =>
  parts
    .map((part) => ({ part, said: partText(part) }))
    .filter(({ part, said }) => !(part.dropWhenEmpty && said === ''))
    .map(({ said }) => said)
    .join('\n')

/**
 * Every part of a block, in the order the flattened text sets them.
 *
 * The exhaustive switch is what turns a new block kind into a compile error
 * here, the same as it did in `blockText` — an unhandled kind answering empty
 * would make a block invisible to search while looking perfectly fine.
 */
export function textPartsOf(content: BlockContent): TextPart[] {
  switch (content.kind) {
    case 'paragraph':
    case 'run_in_heading':
      return [
        {
          label: 'Text',
          dropWhenEmpty: false,
          segments: [runSeg('content', 'Text', content.content)],
        },
      ]
    /* Its attribution too, the same as a source quote's: a quotation's source
       line is part of the quotation, and the renderer sets it. */
    case 'quotation':
      return [
        {
          label: 'Quotation',
          dropWhenEmpty: true,
          segments: [runSeg('content', 'Quotation', content.content)],
        },
        ...(content.attribution
          ? [
              {
                label: 'Attribution',
                dropWhenEmpty: true,
                segments: [
                  runSeg('attribution', 'Attribution', content.attribution),
                ],
              },
            ]
          : []),
      ]
    case 'claim_list':
      return content.items.map((run, i) => ({
        label: `Claim ${i + 1}`,
        dropWhenEmpty: false,
        segments: [runSeg(`items.${i}`, `Claim ${i + 1}`, run)],
      }))
    /* Its classification too, and LAST. The line renders on the page in both
       the reader and in print, so a phrase visibly there has to be findable —
       and appended after the words rather than among them, because a comment
       anchors by offset into this string and anything inserted earlier would
       move every anchor already taken. */
    case 'verse':
      return [
        ...(content.reference
          ? [
              {
                label: 'Reference',
                dropWhenEmpty: true,
                segments: [runSeg('reference', 'Reference', content.reference)],
              },
            ]
          : []),
        {
          label: 'Hebrew',
          dropWhenEmpty: true,
          segments: [runSeg('he', 'Hebrew', content.he)],
        },
        ...(content.en
          ? [
              {
                label: 'Translation',
                dropWhenEmpty: true,
                segments: [runSeg('en', 'Translation', content.en)],
              },
            ]
          : []),
        {
          label: 'Classification',
          dropWhenEmpty: true,
          segments: [
            {
              of: 'field' as const,
              path: 'classification',
              label: 'Classification',
              value: content.classification ?? '',
            },
          ],
        },
      ]
    case 'source_quote':
      return [
        {
          label: 'Hebrew',
          dropWhenEmpty: false,
          segments: [runSeg('he', 'Hebrew', content.he)],
        },
        {
          label: 'Translation',
          dropWhenEmpty: false,
          segments: [runSeg('en', 'Translation', content.en)],
        },
        ...(content.attribution
          ? [
              {
                label: 'Attribution',
                dropWhenEmpty: false,
                segments: [
                  runSeg('attribution', 'Attribution', content.attribution),
                ],
              },
            ]
          : []),
      ]
    case 'dialogue':
      /* A turn is one LINE, so its speaker, its colon and its paragraphs are
         segments of a single part. The colon and the spaces between paragraphs
         belong to no field: a phrase running across one is findable and is not
         rewritable, which is why a joiner is a segment rather than something
         quietly folded into its neighbor. */
      return content.turns.map((turn, i) => ({
        label: `Turn ${i + 1}`,
        dropWhenEmpty: false,
        segments: [
          /* A turn that names nobody is the speaker before carrying on, so its
             words stand as they are rather than under a colon with nothing in
             front of it. */
          ...(turn.speaker
            ? [
                runSeg(`turns.${i}.speaker`, `Speaker ${i + 1}`, turn.speaker),
                { of: 'joiner' as const, value: ': ' },
              ]
            : []),
          ...turn.content.flatMap((run, j) => [
            ...(j > 0 ? [{ of: 'joiner' as const, value: ' ' }] : []),
            runSeg(`turns.${i}.content.${j}`, `Turn ${i + 1}`, run),
          ]),
        ],
      }))
    case 'figure':
      return content.caption
        ? [
            {
              label: 'Caption',
              dropWhenEmpty: false,
              segments: [runSeg('caption', 'Caption', content.caption)],
            },
          ]
        : []
    case 'divider':
      return []
  }
}

/** Every part of a node, a heading's title and subtitle included. */
export function nodeTextPartsOf(content: NodeContent): TextPart[] {
  if (!isHeading(content)) return textPartsOf(content)
  return [
    {
      label: 'Title',
      dropWhenEmpty: true,
      segments: [runSeg('title', 'Title', content.title)],
    },
    ...(content.subtitle
      ? [
          {
            label: 'Subtitle',
            dropWhenEmpty: true,
            segments: [runSeg('subtitle', 'Subtitle', content.subtitle)],
          },
        ]
      : []),
  ]
}

/**
 * The block's text, for search and for showing a change in a diff.
 */
export function blockText(content: BlockContent): string {
  return linesOf(textPartsOf(content))
}

/**
 * What a node says, whatever kind of node it is.
 *
 * The one answer to "what are the words here", so a passage and a part are
 * searched, diffed and read as text by the same call. A heading answers with its
 * title and subtitle and not its running head, which is a shortened form of the
 * title rather than more words.
 */
export function nodeText(content: NodeContent): string {
  return linesOf(nodeTextPartsOf(content))
}

/**
 * The same text with Hebrew pointing removed.
 *
 * The platform's, rather than a second copy: a stripper here and a stripper
 * beside it are free to disagree about what counts as a mark, and every guard
 * that compares one's output against the other's reports changes that never
 * happened. It settles the encoding first, which is what a copy written from
 * the mark ranges alone cannot do.
 */
export { stripPointing } from '@app/shared'

/**
 * Every call this node makes, in reading order — as KINDS.
 *
 * It cannot answer what they NAME, and that is the point: the words carry no
 * ids. What each one names is a `studio_call` row found by this same position.
 *
 * The one walk behind every question about a node's calls, so a reader
 * resolving markers against rows counts the same things in the same order.
 */
export function callsIn(content: NodeContent): CallKind[] {
  const out: CallKind[] = []
  for (const run of runsIn(content)) {
    for (const n of run) if (n.t === 'call') out.push(n.of)
  }
  return out
}

/**
 * The same block with a note marker at the end of the run a reader ends on.
 *
 * A note exists because a passage calls it, so the marker and the note are one
 * write — and this is the part of that write which decides WHERE in the block
 * the call sits. The end of the principal run is where a note is called from in
 * running prose, and it is the placement an import already reproduces.
 *
 * Null where the kind holds no run a call can sit in. A divider marks nothing
 * and a figure with no caption has no words to mark, so the honest answer is a
 * refusal rather than a marker filed somewhere a reader will not meet it.
 */
/**
 * The block with its PRINCIPAL run rewritten — the run a reader ends on.
 *
 * Which run that is differs by kind and is decided in exactly one place, here,
 * because every question about where a marker goes is the same question. A kind
 * added to the switch is answered for appending and for placing at once; a
 * second copy would be the kind that gained a marker in one and not the other.
 *
 * Null where the kind holds no run a call can sit in. A divider marks nothing
 * and a figure with no caption has no words to mark, so the honest answer is a
 * refusal rather than a marker filed somewhere a reader will not meet it.
 */
export function withPrincipalRun(
  content: BlockContent,
  rewrite: (run: InlineRun) => InlineRun | null,
): BlockContent | null {
  const one = (run: InlineRun, put: (next: InlineRun) => BlockContent) => {
    const next = rewrite(run)
    return next ? put(next) : null
  }
  switch (content.kind) {
    case 'paragraph':
    case 'quotation':
    case 'run_in_heading':
      return one(content.content, (c) => ({ ...content, content: c }))
    case 'claim_list': {
      // The last claim, because a list is called from the proposition the note
      // answers and the last one is where a list's own note belongs.
      const items = [...content.items]
      const last = items.length - 1
      if (last < 0) return null
      return one(items[last] as InlineRun, (c) => {
        items[last] = c
        return { ...content, items }
      })
    }
    case 'verse':
      // The translation carries the call where there is one: a marker inside
      // pointed Hebrew sits among the marks and is read as one of them.
      return content.en
        ? one(content.en, (c) => ({ ...content, en: c }))
        : one(content.he, (c) => ({ ...content, he: c }))
    case 'source_quote':
      return one(content.en, (c) => ({ ...content, en: c }))
    case 'dialogue': {
      const turns = [...content.turns]
      const last = turns.length - 1
      const turn = turns[last]
      if (!turn || turn.content.length === 0) return null
      const runs = [...turn.content]
      return one(runs[runs.length - 1] as InlineRun, (c) => {
        runs[runs.length - 1] = c
        turns[last] = { ...turn, content: runs }
        return { ...content, turns }
      })
    }
    case 'figure':
      return content.caption
        ? one(content.caption, (c) => ({ ...content, caption: c }))
        : null
    case 'divider':
      return null
  }
}

export function appendCall(
  content: BlockContent,
  of: CallKind,
): { content: BlockContent; at: number } | null {
  const call: Inline = { t: 'call', of }
  const append = (run: InlineRun): InlineRun => [...run, call]
  /* WHERE the marker landed, counted as calls rather than as characters —
     because that position IS the call's identity, and the caller has to write
     the row that says what it names. Computed by walking the new content
     rather than guessed, since a marker appended to a verse's translation sits
     before nothing and after everything in its Hebrew. */
  const placed = (next: BlockContent | null) => {
    if (!next) return null
    const before = callsIn(content).length
    const after = callsIn(next).length
    if (after !== before + 1) return null
    /* The first position where the two sequences diverge is where it went; with
       one call added they agree up to it. */
    const was = callsIn(content)
    const now = callsIn(next)
    let at = was.length
    for (let i = 0; i < was.length; i++) {
      if (was[i] !== now[i]) {
        at = i
        break
      }
    }
    return { content: next, at }
  }

  return placed(withPrincipalRun(content, append))
}

/**
 * The same run with a marker after a given number of characters of its words.
 *
 * The split is on the TEXT the run sets, not on its nodes: a phrase an author
 * points at may well straddle two of them, and a caller counting nodes would be
 * counting a thing the words never showed them.
 *
 * A marker falling inside a node divides it, and the halves keep the node's
 * language and marks — the words either side of a marker are the same words
 * they were, and a split that dropped either would set half a phrase in the
 * wrong face.
 */
function callAfterChars(
  run: InlineRun,
  call: Inline,
  chars: number,
): InlineRun | null {
  if (chars < 0) return null
  const out: Inline[] = []
  let seen = 0
  let placed = false
  for (const node of run) {
    if (placed || node.t !== 'text') {
      out.push(node)
      continue
    }
    const ends = seen + node.v.length
    if (ends < chars) {
      out.push(node)
      seen = ends
      continue
    }
    const cut = chars - seen
    if (cut > 0) out.push({ ...node, v: node.v.slice(0, cut) })
    out.push(call)
    const rest = node.v.slice(cut)
    if (rest) out.push({ ...node, v: rest })
    placed = true
    seen = ends
  }
  /* Falling off the end means the passage has fewer words than the caller
     counted, which is a caller working from text this block no longer holds. */
  return placed ? out : null
}

/**
 * The same block with a marker set after a phrase, rather than at the end.
 *
 * What a scholarly text needs and appending cannot give: a paragraph citing
 * three sources carries three markers, each after the clause it answers, and
 * moving them all to the end says something the author did not.
 *
 * The phrase is the address. An offset alone slides onto whatever later
 * occupies it, and a run index makes the caller count nodes rather than words —
 * so the caller quotes the words the marker goes after, which is how a person
 * says it and what an importer already has in hand.
 *
 * Refused rather than guessed where the phrase is absent, or occurs more than
 * once: a marker on the wrong clause reads as the author's own citation and is
 * the fault nobody would look for.
 */
export function placeCall(
  content: BlockContent,
  of: CallKind,
  after: string,
): { content: BlockContent; at: number } | null {
  if (!after) return null
  const call: Inline = { t: 'call', of }
  const place = (run: InlineRun): InlineRun | null => {
    const said = runText(run)
    const first = said.indexOf(after)
    if (first < 0) return null
    if (said.indexOf(after, first + 1) >= 0) return null
    return callAfterChars(run, call, first + after.length)
  }
  const before = callsIn(content)
  const next = withPrincipalRun(content, place)
  if (!next) return null
  const now = callsIn(next)
  if (now.length !== before.length + 1) return null
  let at = before.length
  for (let i = 0; i < before.length; i++) {
    if (before[i] !== now[i]) {
      at = i
      break
    }
  }
  return { content: next, at }
}

/**
 * Every inline run a block holds, in reading order.
 *
 * The ONE place that says which runs each kind carries. Everything asking what
 * a block references, and everything setting a block as one line of text, reads
 * that from here — because the cost of a second copy is a block kind added to
 * one and forgotten in the other, and what "forgotten" looks like is a citation
 * missed by the license gate, or a note printing as a number with nothing after
 * it.
 */
/** One run a node holds: where it lives in the content, and what it is called. */
export type NamedRun = { path: string; label: string; run: InlineRun }

/**
 * Every inline run a node holds, in reading order, each with the path it sits
 * at.
 *
 * **The ONE statement of which runs a kind carries.** Everything that walks a
 * node's words reads it here — what the node references, what it says as text,
 * which runs an editor offers, and which call each marker is. That last one is
 * why the ORDER is load-bearing rather than merely tidy: a call is found by its
 * position among the calls in a text, so a reader taking the runs in a different
 * order than the rows were numbered in hands every marker the wrong target.
 *
 * The paths are here rather than beside a second copy of this list, because that
 * copy is what a quotation's attribution fell through: it was listed as editable
 * and not as a run, so a citation in a source line was rendered on the page and
 * invisible to everything asking what the block referenced.
 */
export function runsOf(content: NodeContent): NamedRun[] {
  const at = (path: string, label: string, run?: InlineRun): NamedRun[] =>
    run ? [{ path, label, run }] : []

  if (isHeading(content)) {
    return [
      ...at('title', 'Title', content.title),
      ...at('subtitle', 'Subtitle', content.subtitle),
    ]
  }
  switch (content.kind) {
    case 'paragraph':
    case 'run_in_heading':
      return at('content', 'Text', content.content)
    case 'quotation':
      return [
        ...at('content', 'Quotation', content.content),
        ...at('attribution', 'Attribution', content.attribution),
      ]
    case 'claim_list':
      return content.items.flatMap((run, i) =>
        at(`items.${i}`, `Claim ${i + 1}`, run),
      )
    case 'verse':
      return [
        ...at('reference', 'Reference', content.reference),
        ...at('he', 'Hebrew', content.he),
        ...at('en', 'Translation', content.en),
      ]
    case 'source_quote':
      return [
        ...at('he', 'Hebrew', content.he),
        ...at('en', 'Translation', content.en),
        ...at('attribution', 'Attribution', content.attribution),
      ]
    case 'dialogue':
      return content.turns.flatMap((turn, i) => [
        ...at(`turns.${i}.speaker`, `Speaker ${i + 1}`, turn.speaker),
        ...turn.content.flatMap((run, j) =>
          at(`turns.${i}.content.${j}`, `Turn ${i + 1}`, run),
        ),
      ])
    case 'figure':
      return at('caption', 'Caption', content.caption)
    case 'divider':
      return []
  }
}

/**
 * The calls each run makes, aligned one-for-one with `runsOf`.
 *
 * A pure slicing rather than a cursor something advances, so a caller that
 * renders twice — React does — resolves the same marker to the same call both
 * times. Which call a marker is, is still its position; this only says where
 * each run's share of that sequence begins and ends.
 */
export function callsByRun(content: NodeContent, calls: Call[]): Call[][] {
  let from = 0
  return runsOf(content).map(({ run }) => {
    const made = run.filter((n) => n.t === 'call').length
    const mine = calls.slice(from, from + made)
    from += made
    return mine
  })
}

export const runsIn = (content: NodeContent): InlineRun[] =>
  runsOf(content).map((r) => r.run)

/**
 * The calls made by ONE run of a node's words.
 *
 * For a surface that sets a single run rather than the whole node — a note's
 * body drawn as one line apiece. Matched by identity rather than by index, so a
 * caller that already holds the run it is drawing does not have to know its
 * position among the others.
 */
export function callsForRun(
  content: NodeContent,
  calls: Call[],
  run: InlineRun,
): Call[] {
  const at = runsOf(content).findIndex((r) => r.run === run)
  return at < 0 ? [] : (callsByRun(content, calls)[at] ?? [])
}

/**
 * The same content, with one run written at the path `runsOf` named it by.
 *
 * Written from the PATH rather than as a switch over the kinds, so a kind that
 * gains a run gains this for nothing. A switch would be a second statement of
 * which runs a kind holds, which is the split `runsOf` exists to end — and ONE
 * walk for every caller is that same rule applied to the walk itself: a second
 * copy of it is kept in step by hand until the day it is not.
 *
 * **It writes what it was given, an empty run included.** An editor feeds this
 * on every keystroke and renders what comes back, so a field that vanished the
 * moment it held nothing would vanish under the caret of anybody selecting a
 * name and typing a new one. What an empty run MEANS is a question about
 * saving, and it is asked at the save — `asStored`.
 *
 * `held` false is that save's other answer, for a field the model refuses to
 * hold empty: the field is written OUT of the object it sits on rather than
 * left standing there with nothing in it. A position in a LIST is dropped by
 * neither, because removing one renumbers its neighbors and which call a
 * marker is, is its position among them.
 *
 * Generic over the content, because a draft on its way through an editor claims
 * a kind and need not yet be legal. The walk reads the path and nothing else.
 */
export function withRunAt<T extends { kind: string }>(
  content: T,
  path: string,
  run: InlineRun,
  held = true,
): T {
  const keys = path.split('.')
  /* A structural walk over the fields a kind declares. The path came from
     `runsOf` reading this same content, so every key on it is there. */
  const put = (node: unknown, depth: number): unknown => {
    const key = keys[depth] as string
    const last = depth === keys.length - 1
    if (Array.isArray(node)) {
      const copy = [...node]
      const i = Number(key)
      copy[i] = last ? run : put(node[i], depth + 1)
      return copy
    }
    const obj = node as Record<string, unknown>
    if (!last) return { ...obj, [key]: put(obj[key], depth + 1) }
    if (held) return { ...obj, [key]: run }
    const { [key]: _gone, ...without } = obj
    return without
  }
  return put(content, 0) as T
}
